/**
 * Auth Selectors
 * Memoized selectors for auth state
 */

import { createSelector } from '@spcc/core'
import type {
  AuthState,
  AuthUser,
  AuthStatus,
  TenantChannel,
  AuthLoadingState,
} from '../domain/types'
import { canAccessAdminPortal, ADMIN_ROLE_TYPES } from '../domain/utils'

// ============================================================================
// State Type (for use in apps)
// ============================================================================

interface RootStateWithAuth {
  auth: AuthState
}

// ============================================================================
// Base Selectors
// ============================================================================

export const selectAuthState = (state: RootStateWithAuth): AuthState => state.auth

export const selectAuthUser = (state: RootStateWithAuth): AuthUser | null => state.auth.user

export const selectAuthToken = (state: RootStateWithAuth): string | null => state.auth.token

export const selectAuthStatus = (state: RootStateWithAuth): AuthStatus => state.auth.status

export const selectAuthError = (state: RootStateWithAuth): string | null => state.auth.error

export const selectAuthIsInitialized = (state: RootStateWithAuth): boolean => state.auth.isInitialized

export const selectRegistrationStatus = (state: RootStateWithAuth) => state.auth.registrationStatus

export const selectRegistrationMessage = (state: RootStateWithAuth): string | null =>
  state.auth.registrationMessage

export const selectPasswordResetStatus = (state: RootStateWithAuth) => state.auth.passwordResetStatus

export const selectPasswordResetMessage = (state: RootStateWithAuth): string | null =>
  state.auth.passwordResetMessage

export const selectChangePasswordStatus = (state: RootStateWithAuth) => state.auth.changePasswordStatus

export const selectChangePasswordMessage = (state: RootStateWithAuth): string | null =>
  state.auth.changePasswordMessage

export const selectChangePasswordOtpStatus = (state: RootStateWithAuth) => state.auth.changePasswordOtpStatus

export const selectChangePasswordOtpMessage = (state: RootStateWithAuth): string | null =>
  state.auth.changePasswordOtpMessage

export const selectAuthLoading = (state: RootStateWithAuth): AuthLoadingState => state.auth.loading

// ============================================================================
// Derived Selectors
// ============================================================================

/**
 * Check if user is authenticated
 */
export const selectIsAuthenticated = createSelector(
  [selectAuthStatus],
  (status): boolean => status === 'authenticated'
)

/**
 * Check if auth is loading
 */
export const selectIsAuthLoading = createSelector(
  [selectAuthStatus],
  (status): boolean => status === 'loading'
)

/**
 * Check if registration finished successfully
 */
export const selectRegistrationSuccess = createSelector(
  [selectRegistrationStatus],
  (status): boolean => status === 'success'
)

/**
 * Check if password reset finished successfully
 */
export const selectPasswordResetSuccess = createSelector(
  [selectPasswordResetStatus],
  (status): boolean => status === 'success'
)

/**
 * Check if password change finished successfully
 */
export const selectChangePasswordSuccess = createSelector(
  [selectChangePasswordStatus],
  (status): boolean => status === 'success'
)

export const selectChangePasswordOtpSuccess = createSelector(
  [selectChangePasswordOtpStatus],
  (status): boolean => status === 'success'
)

/**
 * Check if password reset is in progress
 */
export const selectIsPasswordResetLoading = createSelector(
  [selectPasswordResetStatus],
  (status): boolean => status === 'requesting'
)

/**
 * Check if there's an auth error
 */
export const selectHasAuthError = createSelector(
  [selectAuthError],
  (error): boolean => error !== null
)

/**
 * Get user email
 */
export const selectUserEmail = createSelector(
  [selectAuthUser],
  (user): string | null => user?.email ?? null
)

/**
 * Get user display name (username or email)
 */
export const selectUserDisplayName = createSelector(
  [selectAuthUser],
  (user): string => {
    if (!user) return ''
    return user.username || user.email
  }
)

/**
 * Check if user is confirmed
 */
export const selectIsUserConfirmed = createSelector(
  [selectAuthUser],
  (user): boolean => user?.confirmed ?? false
)

/**
 * Check if user is blocked
 */
export const selectIsUserBlocked = createSelector(
  [selectAuthUser],
  (user): boolean => user?.blocked ?? false
)

/**
 * Get auth header value for API requests
 */
export const selectAuthHeader = createSelector(
  [selectAuthToken],
  (token): string | null => (token ? `Bearer ${token}` : null)
)

/**
 * Get user's tenant
 */
export const selectUserTenant = createSelector(
  [selectAuthUser],
  (user) => user?.tenant ?? null
)

/**
 * Get user's tenant channel (b2b or ecommerce)
 */
export const selectUserChannel = createSelector(
  [selectAuthUser],
  (user): TenantChannel | null => user?.tenant?.channel ?? null
)

/**
 * Get user's role
 */
export const selectUserRole = createSelector(
  [selectAuthUser],
  (user) => user?.role ?? null
)

/**
 * Get user's role type
 */
export const selectUserRoleType = createSelector(
  [selectUserRole],
  (role): string | null => role?.type ?? null
)

/**
 * Check if user is a super admin or admin (full system access)
 */
export const selectIsSuperAdmin = createSelector(
  [selectUserRole],
  (role): boolean => role?.type === 'super_admin' || role?.type === 'admin'
)

/**
 * Check if user can access admin portal
 */
export const selectCanAccessAdminPortal = createSelector(
  [selectUserRoleType],
  (roleType): boolean => canAccessAdminPortal(roleType)
)

/**
 * Check if user is any type of admin (elevated permissions)
 */
export const selectIsAdmin = createSelector(
  [selectUserRole],
  (role): boolean => ADMIN_ROLE_TYPES.includes(role?.type as any)
)

// ============================================================================
// Loading State Selectors (Specific Operations)
// ============================================================================

/**
 * Check if login is loading
 */
export const selectIsLoginLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.login
)

/**
 * Check if register is loading
 */
export const selectIsRegisterLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.register
)

/**
 * Check if logout is loading
 */
export const selectIsLogoutLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.logout
)

/**
 * Check if password reset is loading
 */
export const selectIsPasswordResetLoadingState = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.passwordReset
)

/**
 * Check if session check is loading
 */
export const selectIsSessionLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.sessionCheck
)

/**
 * Check if profile update is loading
 */
export const selectIsProfileUpdateLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.profileUpdate
)

/**
 * Check if change password is loading
 */
export const selectIsChangePasswordLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.changePassword
)

export const selectIsChangePasswordOtpLoading = createSelector(
  [selectAuthLoading],
  (loading): boolean => loading.changePasswordOtp
)

// ============================================================================
// Lockout Selectors
// ============================================================================

/**
 * Check if account is locked due to too many failed attempts
 */
export const selectIsAttemptBlocked = createSelector(
  [selectAuthState],
  (auth): boolean => auth.isAttemptBlocked
)

/**
 * Get remaining login attempts before lockout (null = unknown, 1-4 = show warning)
 */
export const selectLoginAttemptsRemaining = createSelector(
  [selectAuthState],
  (auth): number | null => auth.loginAttemptsRemaining
)
